import { useDisclaimers } from '@/hooks/queries/disclaimers';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useUserStore } from '@/store/use-user-store';
import {
  buildThinkEducationSurveyFormUrl,
  fetchHasRespondedToThinkEducationSurvey,
  isThinkEducationSurveyClosed,
  markThinkEducationSurveyClosed,
  thinkEducationSurveyConfig,
  type ThinkEducationSurveyConfig,
} from '@/utils/think-education-survey';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

const POLL_INTERVAL_MS = 5000;

/** LE-416: Think Education's Google Form, shown after sign-in, never blocking. */
const ThinkEducationSurveyModal = () => {
  const config = thinkEducationSurveyConfig;
  const { tenantSlug } = useActiveTenant();

  // Other tenants render nothing and make no requests.
  if (!config || !tenantSlug || !config.tenantSlugs.includes(tenantSlug)) {
    return null;
  }

  return <SurveyModal config={config} />;
};

const SurveyModal = ({ config }: { config: ThinkEducationSurveyConfig }) => {
  const user = useUserStore((state) => state.user);
  const displayFirstName = useUserStore((state) => state.displayFirstName);
  const hasAcceptedDisclaimer = useUserStore(
    (state) => state.hasAcceptedDisclaimer
  );
  const { data: disclaimersData, isLoading: isLoadingDisclaimers } =
    useDisclaimers();
  const hasDisclaimer = Boolean(disclaimersData?.results?.[0]);

  const [isOpen, setIsOpen] = useState(false);
  const [wasClosedNow, setWasClosedNow] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const isClosed =
    wasClosedNow ||
    (user?.id != null && isThinkEducationSurveyClosed(user.id));
  const iframeLoadCount = useRef(0);

  const email = user?.email?.trim();
  const formUrl = useMemo(() => {
    if (!email) return '';
    try {
      return buildThinkEducationSurveyFormUrl(config, email);
    } catch {
      // Misconfigured form URL: skip the survey rather than break the Hub.
      return '';
    }
  }, [config, email]);

  const isEligible =
    Boolean(formUrl && user?.id != null) &&
    !isClosed &&
    !isLoadingDisclaimers &&
    (!hasDisclaimer || hasAcceptedDisclaimer);

  const {
    data: hasResponded,
    isFetched,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['think-education-survey', email],
    queryFn: ({ signal }) =>
      fetchHasRespondedToThinkEducationSurvey(config, email!, signal),
    enabled: isEligible,
    retry: 1,
    staleTime: 0,
    refetchInterval: (query) =>
      isOpen && query.state.data !== true ? POLL_INTERVAL_MS : false,
  });

  // Open once the first check settles without a response. A failed check
  // still opens the modal: "Not now" keeps LiFT usable.
  useEffect(() => {
    if (isEligible && isFetched && hasResponded !== true) {
      setIsOpen(true);
    }
  }, [isEligible, isFetched, hasResponded]);

  if (!formUrl) {
    return null;
  }

  const close = () => {
    if (user?.id != null) {
      markThinkEducationSurveyClosed(user.id);
    }
    setWasClosedNow(true);
    setIsOpen(false);
  };

  const handleContinue = () => {
    close();
    toast.success('Thanks. Think Education has your answers.');
  };

  // The iframe reloads when the form navigates (e.g. after Submit), which is
  // the only signal we get from it. Check straight away instead of waiting.
  const handleIframeLoad = () => {
    iframeLoadCount.current += 1;
    if (iframeLoadCount.current > 1 && hasResponded !== true) {
      setIsWaiting(true);
      void refetch();
    }
  };

  const firstName = displayFirstName || user?.first_name;
  let statusText = 'Submit the form, or come back to it later.';
  let statusClass = 'text-default-600';
  if (hasResponded) {
    statusText = firstName
      ? `Response received. Thanks, ${firstName}.`
      : 'Response received. Thanks.';
    statusClass = 'text-success-700';
  } else if (isError) {
    statusText =
      'We can’t confirm your response right now. Use “Not now” to carry on.';
  } else if (isWaiting) {
    statusText = 'Checking for your response…';
  }

  return (
    <Modal
      isOpen={isOpen}
      hideCloseButton
      isDismissable={false}
      isKeyboardDismissDisabled
      size="3xl"
      placement="center"
      classNames={{
        // Above the mobile bottom nav (z-[100]), which otherwise shows over the backdrop.
        backdrop: 'z-[110]',
        wrapper: 'z-[110]',
        // Fit the visible viewport with a small margin on every side, so the
        // footer buttons are always reachable on phones.
        base: 'm-3 h-[calc(100%-1.5rem)] max-w-[720px] overflow-hidden sm:mx-6 sm:my-8 sm:h-[min(780px,calc(100%-4rem))]',
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1.5 border-b border-default-100 px-5 pb-4 pt-5 sm:px-7 sm:pt-6">
          <span className="text-xs font-bold uppercase tracking-wider text-default-500">
            From Think Education
          </span>
          <h2 className="text-xl font-bold">Before you start, a short survey</h2>
          <p className="text-sm font-normal leading-relaxed text-default-600">
            Think Education uses your answers to shape your programme. It takes
            about three minutes, and your answers go to Think Education with
            your name attached.
          </p>
        </ModalHeader>
        <ModalBody className="min-h-0 flex-1 bg-[#F1EEF8] p-0">
          <iframe
            src={formUrl}
            title="Think Education survey"
            className="h-full w-full border-0"
            onLoad={handleIframeLoad}
          />
        </ModalBody>
        <ModalFooter className="flex flex-col items-stretch gap-3 border-t border-default-200 px-5 py-3.5 sm:flex-row sm:px-7 sm:items-center sm:justify-between">
          <div
            className={`flex items-center gap-2 text-sm ${statusClass}`}
            role="status"
            aria-live="polite"
          >
            {isWaiting && !hasResponded && !isError && (
              <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary" />
            )}
            <span>{statusText}</span>
          </div>
          <div className="flex items-center gap-2">
            {!hasResponded && (
              <Button variant="bordered" onPress={close}>
                Not now
              </Button>
            )}
            <Button
              className="ml-auto"
              color="primary"
              isDisabled={!hasResponded}
              onPress={handleContinue}
            >
              Continue to LiFT
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ThinkEducationSurveyModal;
